import { cn } from '@/lib/utils';

import { STEADO_BLADES, STEADO_VIEWBOX } from './steado-geometry';

/**
 * The brand's loading indicator: the mark itself, turning.
 *
 * The mark this replaced was a hexagon, so its loader was a dash chasing the
 * outline - the one motion available to a shape with a fixed "up". Three blades
 * around a common centre have no up: the mark maps onto itself every 120
 * degrees, so turning it a third of a revolution per cycle returns to exactly
 * the frame it started from. The loop has no seam to hide, and nothing is
 * redrawn, stretched or effected - it is the real logo, rotated.
 *
 * That also removes the ghost track the chase needed. A track exists to show
 * how far round the arc has left to travel; a turning solid has no such
 * remainder, and drawing one would be decoration rather than information.
 *
 * Colour it with `text-primary`, not `text-accent`. `--accent` is shadcn's
 * *hover surface* rather than a brand colour: since the retheme it is #26262a,
 * so in dark mode an accent-coloured spinner is near-black on near-black.
 *
 * Under `prefers-reduced-motion` the reset in `index.css` ends the animation
 * after one 0.01 ms iteration, which leaves the mark sitting still at full
 * opacity - a legible logo rather than a frozen mid-frame. The state is carried
 * by the `role="status"` text at the call site, which is why that text is not
 * optional.
 */
export function SteadoLoader({ className }: { readonly className?: string }) {
  return (
    <svg
      viewBox={STEADO_VIEWBOX}
      className={cn('size-4 shrink-0 origin-center animate-turn', className)}
      aria-hidden="true"
      focusable="false"
    >
      {STEADO_BLADES.map((d, i) => (
        <path key={i} d={d} fill="currentColor" />
      ))}
    </svg>
  );
}
